import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Lock, CheckCircle2, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/efs/PageHeader';
import { formatDateTime } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import {
  useConfirmGetitPurchasePrice,
  useGetitPurchasePrice,
  useLockGetitPurchasePrice,
  useRecomputeGetitPurchasePrice,
} from './useGetitPurchasePrice';
import type { GetitPurchasePriceDto, GetitPurchasePriceStatus } from './types';

const NS = 'valuation';
const P = 'efs.valuation.getit-purchase-price';

const statusVariant: Record<GetitPurchasePriceStatus, 'secondary' | 'default' | 'outline'> = {
  draft: 'secondary',
  calculated: 'default',
  confirmed: 'default',
  locked: 'outline',
};

const formatMoney = (value: string | null | undefined): string => {
  if (value == null || value === '') return '-';
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

export const GetitPurchasePricePage = () => {
  const { t } = useTranslation(NS);
  const { batchId } = useParams<{ batchId: string }>();
  const user = useCurrentUser();

  const canRecompute = hasPermission(user, 'valuation.market_price.apply');
  const canConfirm = hasPermission(user, 'quotation.getit.confirm');
  const canLock = hasPermission(user, 'freight.confirm');

  const [lockOpen, setLockOpen] = useState(false);

  const query = useGetitPurchasePrice(batchId);
  const recompute = useRecomputeGetitPurchasePrice(batchId);
  const confirm = useConfirmGetitPurchasePrice(batchId);
  const lock = useLockGetitPurchasePrice(batchId);

  const price = query.data ?? null;
  const isLocked = price?.status === 'locked';

  // Surface the most recent action error. A recompute Conflict means A and/or
  // B is not yet locked (backend: "A not locked" / "B not locked").
  const actionError = recompute.error ?? confirm.error ?? lock.error ?? null;

  const onConfirmLock = async () => {
    if (!price) return;
    await lock.mutateAsync(price.getit_purchase_price_id);
    setLockOpen(false);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t(`${P}.title`)}
        subtitle={t(`${P}.subtitle`)}
        actions={
          batchId ? (
            <>
              {canRecompute ? (
                <Button
                  variant="outline"
                  onClick={() => recompute.mutate()}
                  disabled={recompute.isPending || isLocked}
                >
                  {recompute.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-4 w-4" />
                  )}
                  {t(`${P}.actions.recompute`)}
                </Button>
              ) : null}
              {canConfirm ? (
                <Button
                  variant="outline"
                  onClick={() => price && confirm.mutate(price.getit_purchase_price_id)}
                  disabled={confirm.isPending || !price || price.status !== 'calculated'}
                >
                  {confirm.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                  )}
                  {t(`${P}.actions.confirm`)}
                </Button>
              ) : null}
              {canLock ? (
                <Button
                  onClick={() => setLockOpen(true)}
                  disabled={
                    lock.isPending ||
                    !price ||
                    (price.status !== 'calculated' && price.status !== 'confirmed')
                  }
                >
                  <Lock className="mr-1 h-4 w-4" />
                  {t(`${P}.actions.lock`)}
                </Button>
              ) : null}
            </>
          ) : null
        }
      />

      {/* Compute is blocked until both A and B are locked. */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>{t(`${P}.messages.compute_prerequisites`)}</AlertDescription>
      </Alert>

      {actionError ? (
        <Alert variant="destructive">
          <AlertTitle>{t(`${P}.error.title`)}</AlertTitle>
          <AlertDescription>{actionError.message}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-efs-text-muted">{t(`${P}.messages.loading`)}</p>
      ) : !price ? (
        <Alert>
          <AlertDescription>{t(`${P}.messages.empty`)}</AlertDescription>
        </Alert>
      ) : (
        <PriceCard price={price} />
      )}

      <Dialog open={lockOpen} onOpenChange={setLockOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${P}.lock_confirm.title`)}</DialogTitle>
            <DialogDescription>{t(`${P}.lock_confirm.description`)}</DialogDescription>
          </DialogHeader>
          {lock.error ? (
            <Alert variant="destructive">
              <AlertDescription>{lock.error.message}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockOpen(false)} disabled={lock.isPending}>
              {t(`${P}.actions.cancel`)}
            </Button>
            <Button onClick={onConfirmLock} disabled={lock.isPending}>
              {lock.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t(`${P}.actions.lock`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PriceCard = ({ price }: { price: GetitPurchasePriceDto }) => {
  const { t } = useTranslation(NS);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">{t(`${P}.summary.title`)}</CardTitle>
        <Badge variant={statusVariant[price.status]}>
          {t(`${P}.status.${price.status}`, { defaultValue: price.status })}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Metric label={t(`${P}.summary.value_a`)} value={formatMoney(price.value_a)} />
          <Metric label={t(`${P}.summary.value_b`)} value={formatMoney(price.value_b)} />
        </div>

        {/* Freight C = freight_unit_price × dispatch_trip_count */}
        <div className="rounded-md border border-efs-border bg-efs-bg-subtle p-3">
          <p className="text-xs text-efs-text-muted">{t(`${P}.summary.value_c`)}</p>
          <p className="mt-1 font-mono text-sm tabular-nums">
            {formatMoney(price.freight_unit_price)}
            <span className="mx-1 text-efs-text-muted">×</span>
            {price.dispatch_trip_count ?? '-'}
            <span className="mx-1 text-efs-text-muted">=</span>
            <span className="font-semibold">{formatMoney(price.value_c)}</span>
          </p>
          <p className="mt-1 text-xs text-efs-text-muted">
            {t(`${P}.fields.freight_unit_price`)} × {t(`${P}.fields.dispatch_trip_count`)}
          </p>
        </div>

        {/* D = A − B − C */}
        <div className="rounded-md border border-efs-border p-3">
          <p className="text-xs text-efs-text-muted">{t(`${P}.summary.value_d`)}</p>
          <p className="mt-1 font-mono text-lg font-semibold tabular-nums">
            {formatMoney(price.value_d)}
          </p>
          <p className="mt-1 text-xs text-efs-text-muted">{t(`${P}.summary.value_d_formula`)}</p>
        </div>

        {price.confirmed_at || price.locked_at ? (
          <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-efs-border pt-3 text-xs text-efs-text-muted">
            {price.confirmed_at ? (
              <span>
                {t(`${P}.fields.confirmed_at`)}: {formatDateTime(price.confirmed_at)}
              </span>
            ) : null}
            {price.locked_at ? (
              <span>
                {t(`${P}.fields.locked_at`)}: {formatDateTime(price.locked_at)}
              </span>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-xs text-efs-text-muted">{label}</p>
    <p className="font-mono text-lg font-semibold tabular-nums">{value}</p>
  </div>
);

export default GetitPurchasePricePage;
